<template>
  <button
    type="button"
    class="back-button tap-44"
    :class="[
      `back-button--${variant}`,
      { 'back-button--labeled': hasLabel }
    ]"
    :aria-label="accessibleName"
    @click="emit('click', $event)"
  >
    <i class="pi pi-chevron-left back-button__icon" aria-hidden="true" />
    <span v-if="hasLabel" class="back-button__label">{{ label }}</span>
  </button>
</template>

<script setup>
import { computed } from 'vue';

// Shared page / gallery / plain back control: chevron plus an optional label on
// one line. Labels never wrap inside the hit target — the control widens into a
// pill, then ellipsizes, rather than stacking two lines in a circle.
//
// Presentation only. It reports `click`; the page decides where back goes.
defineOptions({ name: 'BackButton' });

const props = defineProps({
  // Visible label beside the chevron. Empty / null → icon-only circle.
  label: { type: String, default: null },
  // Accessible name when the label is omitted or when it should differ from the
  // visible text. Defaults to `label`, then 'Back'.
  ariaLabel: { type: String, default: null },
  // Visual treatment:
  //   filled — PageHeader glass disc / pill (default)
  //   glass  — translucent control over media (Vehicle Detail gallery)
  //   plain  — text + chevron, no fill (Find a Sale Pattern B)
  variant: {
    type: String,
    default: 'filled',
    validator: (value) => ['filled', 'glass', 'plain'].includes(value),
  },
});

const emit = defineEmits(['click']);

const hasLabel = computed(() => typeof props.label === 'string' && props.label.trim() !== '');

const accessibleName = computed(() => {
  if (typeof props.ariaLabel === 'string' && props.ariaLabel.trim() !== '') return props.ariaLabel;
  if (hasLabel.value) return props.label;
  return 'Back';
});
</script>

<style scoped>
.back-button {
  box-sizing: border-box;
  position: relative;
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 36px;
  height: 36px;
  max-width: 100%;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  line-height: 1;
}

/* The button is drawn at 36 (38 as glass) and ANSWERS across 44, the phone's
   floor, out of the shared `tap-44` class on the element above — a
   pseudo-element that paints nothing and takes no room in the row. Centered, so
   the drawn disc does not move; the extra 4px a side reach into the header's
   own padding and its column gaps, where nothing else presses. The rule used to
   be written out here; it is one mechanism now, in
   themes/src/variables.css. */

.back-button__icon {
  flex: 0 0 auto;
  font-size: var(--p-back-button-icon-font-size);
}

.back-button__label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-back-button-label-font-size);
  font-weight: var(--p-back-button-label-font-weight);
  letter-spacing: var(--p-back-button-label-letter-spacing);
  line-height: 1;
}

/* Labeled: grow into a pill; never stack the label under the chevron. */
.back-button--labeled {
  width: auto;
  min-width: 36px;
  max-width: min(100%, 14rem);
  padding: 0 12px 0 8px;
}

/* --- filled (PageHeader) --- */
.back-button--filled {
  background: var(--p-back-button-filled-background);
  color: var(--p-back-button-filled-color);
  border-radius: var(--p-back-button-filled-border-radius);
}

.back-button--filled .back-button__icon {
  font-size: var(--p-back-button-filled-icon-font-size);
}

/* --- glass (over media) --- */
.back-button--glass {
  min-width: 38px;
  height: 38px;
  background: rgba(0, 0, 0, 0.4);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  border: 0.5px solid rgba(255, 255, 255, 0.14);
  color: var(--p-back-button-glass-color);
}

.back-button--glass.back-button--labeled {
  min-width: 44px;
  padding: 0 12px 0 8px;
}

.back-button--glass .back-button__icon {
  font-size: 16px;
}

/* --- plain (text link style) --- */
.back-button--plain {
  min-width: 0;
  height: auto;
  min-height: 36px;
  padding: 0 6px;
  margin-left: -6px;
  background: transparent;
  color: var(--p-back-button-plain-color);
  border-radius: 8px;
}

.back-button--plain.back-button--labeled {
  max-width: min(100%, 16rem);
  padding: 0 6px;
}

.back-button--plain .back-button__icon {
  font-size: 17px;
}

.back-button--plain .back-button__label {
  font-size: 17px;
}
</style>
